import { useEffect, useState } from "react"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Typography,
} from "@mui/material"

import { useAppDispatch } from "../../../app/hooks"
import { showSuccess } from "../../notifications/notificationsSlice"
import {
    deleteDocumentWorkflowStage,
    DocumentTeamMemberDto,
    DocumentWorkflowDefinitionDto,
    DocumentWorkflowStageDto,
    loadDocumentWorkflowDefinition,
} from "./documentWorkflowApi"
import {
    SHOW_CURRENT_STAGE_TOGGLE_BUTTON,
    stageTypeStyles,
} from "./DocumentWorkflowConfig"
import {
    getStageMembers,
    getStageUiType,
    mapAvailableUser,
    mapStageStatusToUi,
} from "./documentWorkflowHelpers"
import type {
    AvailableWorkflowUser,
    WorkflowMemberViewModel,
    WorkflowStageMemberToDelete,
    WorkflowViewMode,
} from "./documentWorkflowTypes"
import { CurrentStageOverview } from "./components/currentStage/CurrentStageOverview"
import { WorkflowDefinitionView } from "./components/definition/WorkflowDefinitionView"
import { TeamSummaryTable } from "./components/team/TeamSummaryTable"
import { WorkflowStageMembersCard } from "./components/team/WorkflowStageMembersCard"
import { WorkflowViewSwitcher } from "./components/WorkflowViewSwitcher"
import { AddTeamMemberDialog } from "./dialogs/AddTeamMemberDialog"
import { AddWorkflowStageMemberDialog } from "./dialogs/AddWorkflowStageMemberDialog"
import { ChangeWorkflowActionDeadlineDialog } from "./dialogs/ChangeWorkflowActionDeadline"
import { DeleteWorkflowStageMemberDialog } from "./dialogs/DeleteWorkflowStageMemberDialog"
import { DeleteWorkflowTeamMemberDialog } from "./dialogs/DeleteWorkflowTeamMemberDialog"
import { SendWorkflowPendingActionReminder } from "./dialogs/SendWorkflowPendingActionReminder"

export type DocumentTeamMembersViewProps = {
    documentId?: number | string | null
    documentStatus?: string | null
    refreshTrigger?: number
    onExtendMemberDeadline?: (
        stageId: number,
        documentTeamMemberId: number
    ) => void
    onExtendAllDeadlines?: (stageId: number) => void
}

export const DocumentTeamMembersView = ({
    documentId,
    documentStatus,
    refreshTrigger,
    onExtendMemberDeadline,
    onExtendAllDeadlines,
}: DocumentTeamMembersViewProps) => {
    const dispatch = useAppDispatch()

    const [sections, setSections] = useState<DocumentWorkflowStageDto[]>([])
    const [workflowTeamMembers, setWorkflowTeamMembers] =
        useState<DocumentTeamMemberDto[]>([])
    const [availableUsers, setAvailableUsers] =
        useState<AvailableWorkflowUser[]>([])
    const [currentStageId, setCurrentStageId] = useState<number | null>(null)

    const [viewMode, setViewMode] =
        useState<WorkflowViewMode>("overview")
    const [showSetCurrentStageAction, setShowSetCurrentStageAction] =
        useState(false)

    const [teamMemberDialogOpen, setTeamMemberDialogOpen] = useState(false)
    const [teamMemberDialogMode, setTeamMemberDialogMode] =
        useState<"new" | "edit">("new")
    const [selectedTeamMemberEmail, setSelectedTeamMemberEmail] =
        useState("")
    const [selectedStageForMemberAdd, setSelectedStageForMemberAdd] =
        useState<number | null>(null)

    const [teamMemberEmailToDelete, setTeamMemberEmailToDelete] =
        useState<string | null>(null)
    const [stageMemberToDelete, setStageMemberToDelete] =
        useState<WorkflowStageMemberToDelete | null>(null)
    const [changeDeadlineDialogState, setChangeDeadlineDialogState] = useState<{
        mode: "all" | "selected"
        stageId: number
        selectedTeamMemberId?: number | null
    } | null>(null)
    const [sendReminderDialogState, setSendReminderDialogState] = useState<{
        mode: "all" | "selected"
        stageId: number
        selectedTeamMemberId?: number | null
    } | null>(null)

    const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false)
    const [workflowLoadError, setWorkflowLoadError] =
        useState<string | null>(null)

    const applyWorkflowDefinition = (
        definition: DocumentWorkflowDefinitionDto
    ) => {
        const sortedStages = [...definition.stages].sort(
            (left, right) => left.order - right.order
        )

        const currentStageFromDefinition =
            definition.currentWorkflowStageId !== null
                && definition.currentWorkflowStageId !== undefined
                ? sortedStages.find(
                    stage => stage.id === definition.currentWorkflowStageId
                )
                : undefined

        const currentStageFromFlag = sortedStages.find(
            stage => stage.isCurrent
        )
        const currentStageFromStatus = sortedStages.find(
            stage => mapStageStatusToUi(stage.status).kind === "inProgress"
        )

        setSections(sortedStages)
        setWorkflowTeamMembers(definition.teamMembers)
        setAvailableUsers(definition.teamMembers.map(mapAvailableUser))
        setCurrentStageId(
            currentStageFromDefinition?.id
            ?? currentStageFromFlag?.id
            ?? currentStageFromStatus?.id
            ?? null
        )
    }

    const reloadWorkflowDefinition = async () => {
        if (!documentId) {
            return null
        }

        const definition = await dispatch(
            loadDocumentWorkflowDefinition(String(documentId))
        ).unwrap()

        applyWorkflowDefinition(definition)
        return definition
    }

    useEffect(() => {
        let active = true

        const loadWorkflow = async () => {
            if (!documentId) {
                setSections([])
                setWorkflowTeamMembers([])
                setAvailableUsers([])
                setCurrentStageId(null)
                setWorkflowLoadError(null)
                return
            }

            setIsLoadingWorkflow(true)
            setWorkflowLoadError(null)

            try {
                const definition = await dispatch(
                    loadDocumentWorkflowDefinition(String(documentId))
                ).unwrap()

                if (active) {
                    applyWorkflowDefinition(definition)
                }
            } catch {
                if (active) {
                    setWorkflowLoadError(
                        "Nie udalo sie pobrac definicji workflow dokumentu."
                    )
                    setSections([])
                    setWorkflowTeamMembers([])
                    setAvailableUsers([])
                    setCurrentStageId(null)
                }
            } finally {
                if (active) {
                    setIsLoadingWorkflow(false)
                }
            }
        }

        void loadWorkflow()

        return () => {
            active = false
        }
    }, [dispatch, documentId, refreshTrigger])

    const handleWorkflowStageSaved = async () => {
        await reloadWorkflowDefinition()
        setWorkflowLoadError(null)
    }

    const handleRemoveStage = async (stageId: number) => {
        if (!documentId) {
            return false
        }

        try {
            await dispatch(
                deleteDocumentWorkflowStage({
                    documentId: String(documentId),
                    stageId: String(stageId),
                })
            ).unwrap()

            await reloadWorkflowDefinition()
            setWorkflowLoadError(null)
            return true
        } catch {
            setWorkflowLoadError(
                "Nie udalo sie usunac etapu workflow."
            )
            return false
        }
    }

    const handleOpenAddTeamMemberDialog = () => {
        setTeamMemberDialogMode("new")
        setSelectedTeamMemberEmail("")
        setSelectedStageForMemberAdd(null)
        setTeamMemberDialogOpen(true)
    }

    const handleOpenEditTeamMemberDialog = (email: string) => {
        setTeamMemberDialogMode("edit")
        setSelectedTeamMemberEmail(email)
        setSelectedStageForMemberAdd(null)
        setTeamMemberDialogOpen(true)
    }

    const handleOpenAddStageMemberDialog = (stageId: number) => {
        setTeamMemberDialogMode("new")
        setSelectedTeamMemberEmail("")
        setSelectedStageForMemberAdd(stageId)
        setTeamMemberDialogOpen(true)
    }

    const handleCloseTeamMemberDialogs = () => {
        setTeamMemberDialogOpen(false)
        setSelectedTeamMemberEmail("")
        setSelectedStageForMemberAdd(null)
    }

    const handleOpenDeleteTeamMemberDialog = (email: string) => {
        setTeamMemberEmailToDelete(email)
    }

    const handleCloseDeleteTeamMemberDialog = () => {
        setTeamMemberEmailToDelete(null)
    }

    const handleOpenDeleteStageMemberDialog = (
        stageId: number,
        stageTitle: string,
        member: WorkflowMemberViewModel
    ) => {
        setStageMemberToDelete({
            stageId,
            stageTitle,
            member,
        })
    }

    const handleCloseDeleteStageMemberDialog = () => {
        setStageMemberToDelete(null)
    }

    const handleOpenDeleteCurrentStageMemberDialog = (
        stageId: number,
        documentTeamMemberId: number
    ) => {
        const stage = sections.find(section => section.id === stageId)

        if (!stage) {
            return
        }

        const member = getStageMembers(stage, workflowTeamMembers)
            .find(stageMember => stageMember.id === documentTeamMemberId)

        if (member) {
            handleOpenDeleteStageMemberDialog(
                stageId,
                stage.name,
                member
            )
        }
    }

    const handleOpenChangeAllDeadlinesDialog = (stageId: number) => {
        if (onExtendAllDeadlines) {
            onExtendAllDeadlines(stageId)
            return
        }

        setChangeDeadlineDialogState({
            mode: "all",
            stageId,
        })
    }

    const handleOpenChangeMemberDeadlineDialog = (
        stageId: number,
        documentTeamMemberId: number
    ) => {
        if (onExtendMemberDeadline) {
            onExtendMemberDeadline(stageId, documentTeamMemberId)
            return
        }

        setChangeDeadlineDialogState({
            mode: "selected",
            stageId,
            selectedTeamMemberId: documentTeamMemberId,
        })
    }

    const handleCloseChangeDeadlineDialog = () => {
        setChangeDeadlineDialogState(null)
    }

    const handleOpenSendReminderToAllDialog = (stageId: number) => {
        setSendReminderDialogState({
            mode: "all",
            stageId,
        })
    }

    const handleOpenSendReminderToMemberDialog = (
        stageId: number,
        documentTeamMemberId: number
    ) => {
        setSendReminderDialogState({
            mode: "selected",
            stageId,
            selectedTeamMemberId: documentTeamMemberId,
        })
    }

    const handleCloseSendReminderDialog = () => {
        setSendReminderDialogState(null)
    }

    const handleMoveStage = (
        stageId: number,
        direction: "up" | "down"
    ) => {
        setSections(currentSections => {
            const sourceIndex = currentSections.findIndex(
                section => section.id === stageId
            )

            if (sourceIndex < 0) {
                return currentSections
            }

            const targetIndex = direction === "up"
                ? sourceIndex - 1
                : sourceIndex + 1

            if (
                targetIndex < 0
                || targetIndex >= currentSections.length
            ) {
                return currentSections
            }

            const sourceStage = currentSections[sourceIndex]
            const targetStage = currentSections[targetIndex]
            const sourceStageType = getStageUiType(sourceStage)
            const targetStageType = getStageUiType(targetStage)

            if (
                sourceStageType === "EDIT"
                || targetStageType === "EDIT"
                || sourceStageType !== targetStageType
            ) {
                return currentSections
            }

            const nextSections = [...currentSections]
            nextSections[sourceIndex] = targetStage
            nextSections[targetIndex] = sourceStage

            return nextSections
        })
    }

    const selectedStageForMemberDialog =
        selectedStageForMemberAdd === null
            ? null
            : sections.find(
                section => section.id === selectedStageForMemberAdd
            ) ?? null

    const isStageMemberDialogOpen =
        teamMemberDialogOpen
        && teamMemberDialogMode === "new"
        && selectedStageForMemberDialog !== null

    const isGeneralMemberDialogOpen =
        teamMemberDialogOpen
        && !isStageMemberDialogOpen

    const selectedUser = availableUsers.find(
        user => user.email === selectedTeamMemberEmail
    )

    const selectedTeamMemberStageIds = sections
        .filter(section => {
            return selectedUser
                ? section.documentTeamMemberIds.includes(selectedUser.id)
                : false
        })
        .map(section => section.id)

    const teamMemberUserToDelete = teamMemberEmailToDelete
        ? availableUsers.find(
            availableUser => availableUser.email === teamMemberEmailToDelete
        )
        : null

    const teamMemberToDelete = teamMemberUserToDelete
        ? workflowTeamMembers.find(teamMember => {
            return teamMember.userName === teamMemberUserToDelete.userName
                || teamMember.id === teamMemberUserToDelete.id
        }) ?? null
        : null

    const handleStageMemberAdded = (
        definition: DocumentWorkflowDefinitionDto
    ) => {
        applyWorkflowDefinition(definition)
        setWorkflowLoadError(null)
        handleCloseTeamMemberDialogs()
        dispatch(showSuccess("saved"))
    }

    return (
        <Box sx={{ px: 0.5, py: 1 }}>
            {!documentId && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    Brak identyfikatora dokumentu - nie mozna pobrac workflow.
                </Alert>
            )}

            {workflowLoadError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {workflowLoadError}
                </Alert>
            )}

            {isLoadingWorkflow && (
                <Paper
                    variant="outlined"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        mb: 1,
                    }}
                >
                    <CircularProgress size={18} />
                    <Typography variant="body2">
                        Ladowanie definicji workflow...
                    </Typography>
                </Paper>
            )}

            {SHOW_CURRENT_STAGE_TOGGLE_BUTTON && (
                <Box sx={{ display: "flex", mb: 1 }}>
                    <Button
                        variant={
                            showSetCurrentStageAction
                                ? "contained"
                                : "outlined"
                        }
                        size="small"
                        color="info"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => setShowSetCurrentStageAction(
                            currentValue => !currentValue
                        )}
                    >
                        {showSetCurrentStageAction
                            ? "Ukryj wybor aktywnego etapu"
                            : "Pokaz wybor aktywnego etapu"}
                    </Button>
                </Box>
            )}

            <WorkflowViewSwitcher
                value={viewMode}
                onChange={setViewMode}
            />

            {documentId
                && !isLoadingWorkflow
                && !workflowLoadError
                && viewMode === "overview"
                && (
                    <>
                        <CurrentStageOverview
                            documentId={documentId}
                            sections={sections}
                            currentStageId={currentStageId}
                            documentStatus={documentStatus}
                            onAddMember={handleOpenAddStageMemberDialog}
                            onRemoveMember={
                                handleOpenDeleteCurrentStageMemberDialog
                            }
                            onExtendMemberDeadline={
                                handleOpenChangeMemberDeadlineDialog
                            }
                            onExtendAllDeadlines={
                                handleOpenChangeAllDeadlinesDialog
                            }
                            onSendReminderToAll={
                                handleOpenSendReminderToAllDialog
                            }
                            onSendReminderToMember={
                                handleOpenSendReminderToMemberDialog
                            }
                        />
                        <Divider sx={{ m: 2 }} />
                    </>
                )}

            <DeleteWorkflowTeamMemberDialog
                open={teamMemberEmailToDelete !== null}
                documentId={documentId}
                userName={teamMemberToDelete?.userName}
                onSuccess={definition => {
                    applyWorkflowDefinition(definition)
                    setWorkflowLoadError(null)
                    setTeamMemberEmailToDelete(null)
                    dispatch(showSuccess("deleted"))
                }}
                onClose={handleCloseDeleteTeamMemberDialog}
            />

            <DeleteWorkflowStageMemberDialog
                open={stageMemberToDelete !== null}
                documentId={documentId}
                stageId={stageMemberToDelete?.stageId}
                userName={stageMemberToDelete?.member.userName}
                memberName={stageMemberToDelete?.member.name}
                stageTitle={stageMemberToDelete?.stageTitle}
                onSuccess={definition => {
                    applyWorkflowDefinition(definition)
                    setWorkflowLoadError(null)
                    setStageMemberToDelete(null)
                    dispatch(showSuccess("deleted"))
                }}
                onCancel={handleCloseDeleteStageMemberDialog}
            />

            {viewMode === "summary" && (
                <TeamSummaryTable
                    sections={sections}
                    teamMembers={workflowTeamMembers}
                    onAddTeamMemberClick={handleOpenAddTeamMemberDialog}
                    onEditTeamMemberClick={handleOpenEditTeamMemberDialog}
                    onRemoveTeamMember={handleOpenDeleteTeamMemberDialog}
                />
            )}

            {viewMode === "stages" && sections.map((section, index) => {
                const stageType = getStageUiType(section)
                const stageStatus = mapStageStatusToUi(section.status)
                const isActiveStage =
                    section.isCurrent
                    || section.id === currentStageId
                    || stageStatus.kind === "inProgress"

                return (
                    <Box
                        key={section.id}
                        sx={{
                            position: "relative",
                            pl: 1.5,
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                left: 0.25,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                borderRadius: 999,
                                bgcolor: stageTypeStyles[stageType].border,
                                opacity: 0.95,
                            },
                        }}
                    >
                        <WorkflowStageMembersCard
                            stageId={section.id}
                            order={index + 1}
                            title={section.name}
                            description={section.description}
                            stageType={stageType}
                            isActiveStage={isActiveStage}
                            members={getStageMembers(
                                section,
                                workflowTeamMembers
                            )}
                            onAddMemberClick={
                                handleOpenAddStageMemberDialog
                            }
                            onRemoveMemberClick={
                                handleOpenDeleteStageMemberDialog
                            }
                        />
                    </Box>
                )
            })}

            {viewMode === "wfl" && (
                <WorkflowDefinitionView
                    documentId={documentId}
                    sections={sections}
                    currentStageId={currentStageId}
                    showSetCurrentStageAction={showSetCurrentStageAction}
                    onStageSaved={handleWorkflowStageSaved}
                    onRemoveStage={handleRemoveStage}
                    onMoveStage={handleMoveStage}
                    onSetCurrentStage={setCurrentStageId}
                />
            )}

            {isStageMemberDialogOpen && selectedStageForMemberDialog && (
                <AddWorkflowStageMemberDialog
                    documentId={documentId}
                    stage={selectedStageForMemberDialog}
                    workflowTeamMembers={workflowTeamMembers}
                    onClose={handleCloseTeamMemberDialogs}
                    onSuccess={handleStageMemberAdded}
                />
            )}

            {isGeneralMemberDialogOpen && (
                <AddTeamMemberDialog
                    mode={teamMemberDialogMode}
                    documentId={documentId}
                    sections={sections}
                    workflowTeamMembers={workflowTeamMembers}
                    initialSelectedUserEmail={selectedTeamMemberEmail}
                    initialSelectedStageIds={selectedTeamMemberStageIds}
                    onClose={handleCloseTeamMemberDialogs}
                    onSuccess={handleStageMemberAdded}
                />
            )}

            {changeDeadlineDialogState && documentId && (() => {
                const dialogStage = sections.find(
                    section => section.id === changeDeadlineDialogState.stageId
                )

                if (!dialogStage) {
                    return null
                }

                return (
                    <ChangeWorkflowActionDeadlineDialog
                        documentId={String(documentId)}
                        stage={dialogStage}
                        mode={changeDeadlineDialogState.mode}
                        selectedTeamMemberId={
                            changeDeadlineDialogState.selectedTeamMemberId
                        }
                        onClose={handleCloseChangeDeadlineDialog}
                        onSuccess={definition => {
                            applyWorkflowDefinition(definition)
                            setWorkflowLoadError(null)
                            handleCloseChangeDeadlineDialog()
                        }}
                    />
                )
            })()}

            {sendReminderDialogState && documentId && (() => {
                const dialogStage = sections.find(
                    section => section.id === sendReminderDialogState.stageId
                )

                if (!dialogStage) {
                    return null
                }

                return (
                    <SendWorkflowPendingActionReminder
                        documentId={String(documentId)}
                        stage={dialogStage}
                        mode={sendReminderDialogState.mode}
                        selectedTeamMemberId={
                            sendReminderDialogState.selectedTeamMemberId
                        }
                        onClose={handleCloseSendReminderDialog}
                        onSuccess={() => {
                            handleCloseSendReminderDialog()
                        }}
                    />
                )
            })()}
        </Box>
    )
}